export type ThemeColourOutput = "suffix" | "css";
export type ThemeColourSource = "built-in" | "custom";
export type ThemeColourGroupId =
  | "surfaces"
  | "content"
  | "actions"
  | "feedback"
  | "controls"
  | "charts"
  | "custom";

export interface ThemeColourChoice {
  name: string;
  label: string;
  source: ThemeColourSource;
  group: ThemeColourGroupId;
  value: string;
}

export interface ThemeColourGroup {
  id: ThemeColourGroupId;
  label: string;
  choices: ThemeColourChoice[];
}

export interface ParsedThemeColour {
  kind: "empty" | "theme" | "literal";
  raw: string;
  name?: string;
}

export const BUILT_IN_THEME_COLOUR_GROUPS = [
  {
    id: "surfaces",
    label: "Surfaces",
    names: [
      "background",
      "card",
      "popover",
    ],
  },
  {
    id: "content",
    label: "Content",
    names: [
      "foreground",
      "card-foreground",
      "popover-foreground",
      "muted",
      "muted-foreground",
    ],
  },
  {
    id: "actions",
    label: "Brand & actions",
    names: [
      "primary",
      "primary-foreground",
      "secondary",
      "secondary-foreground",
      "accent",
      "accent-foreground",
    ],
  },
  {
    id: "feedback",
    label: "Feedback",
    names: [
      "destructive",
      "destructive-foreground",
      "warning",
      "warning-foreground",
      "success",
      "success-foreground",
      "info",
      "info-foreground",
    ],
  },
  {
    id: "controls",
    label: "Controls",
    names: ["border", "input", "ring"],
  },
  {
    id: "charts",
    label: "Charts",
    names: ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5"],
  },
] as const;

export const BUILT_IN_THEME_COLOUR_NAMES = BUILT_IN_THEME_COLOUR_GROUPS
  .flatMap((group) => [...group.names]);

const BUILT_IN_NAME_SET = new Set<string>(BUILT_IN_THEME_COLOUR_NAMES);
const RESERVED_CUSTOM_NAMES = new Set([
  "canvas",
  "dark",
  "light",
  "radius",
  "root",
  "shadow",
  "sidebar",
]);
const RESERVED_CUSTOM_PREFIXES = [
  "cat-",
  "chart-",
  "color-",
  "font-",
  "shadow-",
  "sidebar-",
  "spacing-",
  "tw-",
];
const THEME_NAME_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const LEGACY_LAYOUT_COLOURS: Record<string, string> = {
  white: "#fff",
  "slate-50": "#f8fafc",
  "slate-100": "#f1f5f9",
  "slate-200": "#e2e8f0",
  "gray-50": "#f9fafb",
  "gray-100": "#f3f4f6",
  "primary-50": "color-mix(in oklch, var(--primary) 12%, white)",
  "primary-100": "color-mix(in oklch, var(--primary) 22%, white)",
};

export function themeColourLabel(name: string): string {
  return name
    .split("-")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function extractThemeVariables(css: string | null | undefined): Record<string, string> {
  const variables: Record<string, string> = {};
  const declaration = /(--[\w-]+)\s*:\s*([^;{}]+);/g;
  let match: RegExpExecArray | null;

  while ((match = declaration.exec(css ?? "")) !== null) {
    variables[match[1].slice(2)] = match[2].trim();
  }

  return variables;
}

function isCustomThemeColour(name: string, variables: Record<string, string>): boolean {
  return THEME_NAME_PATTERN.test(name)
    && !BUILT_IN_NAME_SET.has(name)
    && !RESERVED_CUSTOM_NAMES.has(name)
    && !name.endsWith("-foreground")
    && !RESERVED_CUSTOM_PREFIXES.some((prefix) => name.startsWith(prefix))
    && Boolean(variables[name])
    && Boolean(variables[`${name}-foreground`]);
}

export function buildThemeColourChoices(css: string | null | undefined): ThemeColourChoice[] {
  const variables = extractThemeVariables(css);
  const builtIn = BUILT_IN_THEME_COLOUR_GROUPS.flatMap((group) =>
    group.names.map((name) => ({
      name,
      label: themeColourLabel(name),
      source: "built-in" as const,
      group: group.id,
      value: variables[name] ?? `var(--${name})`,
    })));
  const custom = Object.keys(variables)
    .filter((name) => isCustomThemeColour(name, variables))
    .sort((left, right) => left.localeCompare(right))
    .map((name) => ({
      name,
      label: themeColourLabel(name),
      source: "custom" as const,
      group: "custom" as const,
      value: variables[name],
    }));

  return [...builtIn, ...custom];
}

export function parseThemeColour(
  value: unknown,
  options: { output?: ThemeColourOutput; knownNames?: Iterable<string> } = {},
): ParsedThemeColour {
  const raw = String(value ?? "").trim();
  if (!raw) return { kind: "empty", raw: "" };

  const variable = /^var\(\s*--([a-z][\w-]*)\s*\)$/i.exec(raw);
  if (variable) return { kind: "theme", raw, name: variable[1] };

  const legacyClass = /^bg-([a-z][\w-]*)$/i.exec(raw);
  if (legacyClass) return { kind: "theme", raw, name: legacyClass[1] };

  const knownNames = new Set(options.knownNames ?? BUILT_IN_THEME_COLOUR_NAMES);
  if (knownNames.has(raw) || (options.output === "suffix" && THEME_NAME_PATTERN.test(raw))) {
    return { kind: "theme", raw, name: raw };
  }

  return { kind: "literal", raw };
}

export function formatThemeColour(
  value: unknown,
  output: ThemeColourOutput,
  knownNames?: Iterable<string>,
): string {
  const parsed = parseThemeColour(value, { output, knownNames });
  if (parsed.kind === "empty") return "";
  if (parsed.kind === "literal" || !parsed.name) return parsed.raw;
  return output === "suffix" ? parsed.name : `var(--${parsed.name})`;
}

export function themeColourPreview(
  value: unknown,
  output: ThemeColourOutput,
  knownNames?: Iterable<string>,
): string {
  const parsed = parseThemeColour(value, { output, knownNames });
  if (parsed.kind === "empty") return "transparent";
  if (output === "suffix" && parsed.name && LEGACY_LAYOUT_COLOURS[parsed.name]) {
    return LEGACY_LAYOUT_COLOURS[parsed.name];
  }
  return parsed.kind === "theme" && parsed.name ? `var(--${parsed.name})` : parsed.raw;
}

export function useThemeColours() {
  const themeCss = useState<string | null>("admin-theme-css", () => null);
  const choices = computed(() => buildThemeColourChoices(themeCss.value));
  const knownNames = computed(() => choices.value.map((choice) => choice.name));
  const builtInChoices = computed(() => choices.value.filter((choice) => choice.source === "built-in"));
  const customChoices = computed(() => choices.value.filter((choice) => choice.source === "custom"));
  const builtInGroups = computed<ThemeColourGroup[]>(() =>
    BUILT_IN_THEME_COLOUR_GROUPS.map((group) => ({
      id: group.id,
      label: group.label,
      choices: builtInChoices.value.filter((choice) => choice.group === group.id),
    })));

  return {
    themeCss: readonly(themeCss),
    choices,
    knownNames,
    builtInChoices,
    builtInGroups,
    customChoices,
  };
}
